import { createContext, useContext } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { getReports, postDailyReport, postReport, postTimeEntry, putReport, putTimeEntry } from '@/api/reports';
import { route } from 'preact-router';
import globalContext, { useGlobals } from '@/context';
import { createIDBPersister } from './persister';
import { hasApiToken } from '@/api/helpers';
import { queryClient } from './queryClient';
import { useTranslation } from 'react-i18next';
import { appendFormData, isNumeric, upsert } from '@/utils';
import { FC } from 'preact/compat';

const persister = createIDBPersister('reactQuery')

const QueryClientContext = createContext<QueryClient | undefined>(undefined);

export const QueryClientProviderWrapper = ({ children }: { children: FC }): FC => {
    const [s, actions] = useContext(globalContext);
    const { t } = useTranslation();

    queryClient.setMutationDefaults(['create-time-entry'], {
        mutationFn: (data: any) => {
            const formData = new FormData();
            appendFormData(formData, data);
            return isNumeric(Number(formData.get('id'))) ? postTimeEntry(formData) : putTimeEntry(formData);
        },
        onMutate: async (variables: any) => {
            await queryClient.cancelQueries({ queryKey: ['time-entries'] });

            const optimisticDailyReport = variables;

            queryClient.setQueryData(['time-entries'], (oldData: any) => {
                if (!oldData) return [optimisticDailyReport];
                return upsert(oldData, optimisticDailyReport);
            });

            if (!navigator.onLine) {
                actions.toastInfo(t('toasts:form_will_be_sent_when_online'));

                setTimeout(() => {
                    route('/reports');
                }, 1000);
            }

            return { optimisticDailyReport };
        },
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
            actions.toastInfo(t('toasts:form_submitted'));
            setTimeout(() => {
                route('/reports');
            }, 1000);
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['time-entries'], (oldData: any) =>
                oldData.filter((report: any) => report.id !== context.optimisticDailyReport.id)
            );
        },
        retry: 3,
    });

    queryClient.setMutationDefaults(['create-form'], {
        mutationFn: (data: any) => {
            const formData = new FormData();
            appendFormData(formData, data);
            return postReport(formData);
        },
        onMutate: async (variables: any) => {
            await queryClient.cancelQueries({ queryKey: ['forms'] });

            const optimisticForm = { ...variables, status: 'pending' };

            queryClient.setQueryData(['forms'], (oldData: any) => {
                if (!oldData) return [optimisticForm];
                return upsert(oldData, optimisticForm);
            });

            if (!navigator.onLine) {
                actions.toastInfo(t('toasts:form_will_be_sent_when_online'));

                setTimeout(() => {
                    route('/reports');
                }, 1000);
            }

            return { optimisticForm };
        },
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['forms'] });
            actions.toastInfo(t('toasts:form_submitted'));
            setTimeout(() => {
                route('/reports');
            }, 1000);
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['forms'], (oldData: any) =>
                oldData.filter((report: any) => report.id !== context.optimisticDailyReport.id)
            );
        },
        retry: 3,
    });

    queryClient.setMutationDefaults(['update-form'], {
        mutationFn: (data: any) => {
            const formData = new FormData();
            appendFormData(formData, data);
            return putReport(formData);
        },
        onMutate: async (variables: any) => {
            await queryClient.cancelQueries({ queryKey: ['forms'] });

            const optimisticForm = { ...variables, status: 'pending' };

            queryClient.setQueryData(['forms'], (oldData: any) => {
                if (!oldData) return [optimisticForm];
                return upsert(oldData, optimisticForm);
            });

            if (!navigator.onLine) {
                actions.toastInfo(t('toasts:form_will_be_sent_when_online'));

                setTimeout(() => {
                    route('/reports');
                }, 1000);
            }

            return { optimisticForm };
        },
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['forms'] });
            actions.toastInfo(t('toasts:form_submitted'));
            setTimeout(() => {
                route('/reports');
            }, 1000);
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['forms'], (oldData: any) =>
                oldData.filter((report: any) => report.id !== context.optimisticDailyReport.id)
            );
        },
        retry: 3,
    });

    queryClient.setMutationDefaults(['create-daily-report'], {
        mutationFn: (data: any) => {
            const formData = new FormData();
            appendFormData(formData, data);
            return postDailyReport(formData);
        },
        onMutate: async (variables: any) => {
            await queryClient.cancelQueries({ queryKey: ['daily-reports'] });

            const optimisticDailyReport = variables;

            queryClient.setQueryData(['daily-reports'], (oldData: any) => {
                if (!oldData) return [optimisticDailyReport];
                return upsert(oldData, optimisticDailyReport);
            });

            if (!navigator.onLine) {

                actions.toastInfo('toasts:form_will_be_sent_when_online');

                setTimeout(() => {
                    route('/reports');
                }, 1000);
            }

            return { optimisticDailyReport };
        },
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['daily-reports'] });
            actions.toastInfo(t('toasts:form_submitted'));
            setTimeout(() => {
                route('/reports');
            }, 1000);
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['daily-reports'], (oldData: any) =>
                oldData.filter((report: any) => report.id !== context.optimisticDailyReport.id)
            );
        },
        retry: 3,
    });

    return (
        <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{ persister }}
            onSuccess={() => {
                if (hasApiToken()) {
                    queryClient.resumePausedMutations();
                }
            }}
        >
            {children}
        </PersistQueryClientProvider>
    );
};

export const useQueryClientContext = () => {
    const context = useContext(QueryClientContext);
    if (!context) {
        throw new Error('useQueryClientContext must be used within a QueryClientProviderWrapper');
    }
    return context;
};